import { useMemo, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import {
    useServiceTypes,
    useCreateServiceType,
    useUpdateServiceType,
    useDeleteServiceType,
    type ProviderLevel,
    type ServiceType,
    type ServiceTypeWritePayload,
} from '@/api/serviceTypes';
import { useServiceCodes } from '@/api/serviceCodes';
import { usePayers } from '@/api/payers';
import {
    usePayerRates,
    useCreatePayerRate,
    useUpdatePayerRate,
    useDeletePayerRate,
    type PayerRate,
} from '@/api/payerRates';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { FormField, Input } from '@/components/ui/FormField';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
type StatusTab = 'Active' | 'Inactive' | 'All';
const STATUS_TABS: StatusTab[] = ['Active', 'Inactive', 'All'];
const PROVIDER_OPTIONS: ProviderLevel[] = ['RBT', 'BCBA', 'Any'];

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function ServiceTypesPanel() {
    const { data, isLoading, isError, error } = useServiceTypes();
    const createM = useCreateServiceType();
    const updateM = useUpdateServiceType();
    const deleteM = useDeleteServiceType();

    const [tab, setTab] = useState<StatusTab>('Active');
    const [editing, setEditing] = useState<ServiceType | null>(null);
    const [creating, setCreating] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<ServiceType | null>(null);

    const rows = useMemo(() => {
        if (!data) return [];
        const filtered = tab === 'All' ? data : data.filter((r) => r.status === tab);
        return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }, [data, tab]);

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <h2 className="font-display text-lg font-bold text-text">Service Types</h2>
                <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                    <Plus className="w-3.5 h-3.5" /> Add service type
                </Button>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                Billable service definitions. Each one links to a configured service code and a
                provider level (RBT / BCBA / Any) — the same code can appear multiple times so
                each provider level reimburses at its own rate. Payer-specific allowed rates
                live on the Payer Rates tab of each service type.
            </p>

            <Card className="p-0 overflow-hidden">
                <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
                    <div role="tablist" aria-label="Filter service types by status" className="flex items-center gap-1 p-0.5 bg-surface-muted rounded-md">
                        {STATUS_TABS.map((s) => (
                            <button
                                key={s}
                                type="button"
                                role="tab"
                                aria-selected={tab === s}
                                onClick={() => setTab(s)}
                                className={cn(
                                    'px-3 py-1 text-xs rounded transition-colors',
                                    tab === s ? 'bg-surface text-text shadow-xs font-medium' : 'text-text-muted hover:text-text',
                                )}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <div className="ml-auto text-xs text-text-subtle tabular-nums">
                        {rows.length} {rows.length === 1 ? 'service type' : 'service types'}
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load service types</p>
                        <p className="text-xs text-text-subtle">{extractErrorMessage(error, 'Please try again.')}</p>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>{data && data.length > 0 ? 'No service types match the current filter.' : 'No service types yet.'}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold w-28">Service code</th>
                                    <th className="px-3 py-2 font-bold">Name</th>
                                    <th className="px-3 py-2 font-bold w-24">Provider</th>
                                    <th className="px-3 py-2 font-bold text-right w-24">Bill rate</th>
                                    <th className="px-3 py-2 font-bold text-right w-28">Default allowed</th>
                                    <th className="px-3 py-2 font-bold w-28">Status</th>
                                    <th className="px-3 py-2 font-bold w-24 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-surface-muted transition-colors">
                                        <td className="px-3 py-2 font-mono text-xs text-text-muted">{r.code || '—'}</td>
                                        <td className="px-3 py-2 font-medium">{r.name}</td>
                                        <td className="px-3 py-2 text-text-muted">{r.provider || '—'}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{r.billable ? USD.format(r.billRate) : '—'}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-text-muted">{r.billable ? USD.format(r.allowRate) : '—'}</td>
                                        <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                                        <td className="px-3 py-2 text-right">
                                            <Button variant="ghost" size="xs" aria-label={`Edit ${r.name}`} onClick={() => setEditing(r)} className="mr-1">
                                                <Pencil className="w-3 h-3" />
                                            </Button>
                                            <Button variant="ghost" size="xs" aria-label={`Delete ${r.name}`} onClick={() => setConfirmDelete(r)}>
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {(creating || editing) && (
                <ServiceTypeDialog
                    initial={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onCreate={async (payload) => {
                        const created = await createM.mutateAsync(payload);
                        return created;
                    }}
                    onUpdate={async (id, payload) => {
                        await updateM.mutateAsync({ id, payload });
                    }}
                />
            )}

            {confirmDelete && (
                <DeleteConfirmDialog
                    row={confirmDelete}
                    onClose={() => setConfirmDelete(null)}
                    onConfirm={async () => {
                        await deleteM.mutateAsync(confirmDelete.id);
                        setConfirmDelete(null);
                    }}
                />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Service type dialog: Details tab + Payer Rates tab
// ─────────────────────────────────────────────────────────────────────────

type DialogTab = 'details' | 'rates';

function ServiceTypeDialog({
    initial,
    onClose,
    onCreate,
    onUpdate,
}: {
    initial: ServiceType | null;
    onClose: () => void;
    onCreate: (p: ServiceTypeWritePayload) => Promise<ServiceType>;
    onUpdate: (id: string, p: ServiceTypeWritePayload) => Promise<void>;
}) {
    const [tab, setTab] = useState<DialogTab>('details');
    // On create, the service type row doesn't exist until Details is saved —
    // so the Payer Rates tab is disabled until we have an id to bind rates to.
    const [savedId, setSavedId] = useState<string | null>(initial?.id ?? null);

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                aria-describedby={undefined}
                className="w-[min(calc(100vw-2rem),44rem)] h-[36.5rem] flex flex-col overflow-hidden"
            >
                <DialogHeader className="shrink-0">
                    <DialogTitle>{initial ? 'Edit service type' : 'Add service type'}</DialogTitle>
                </DialogHeader>

                <Tabs.Root value={tab} onValueChange={(v) => setTab(v as DialogTab)} className="flex flex-col flex-1 min-h-0">
                    <Tabs.List className="shrink-0 flex gap-1 border-b border-border -mx-5 px-5 mb-4">
                        <Tabs.Trigger
                            value="details"
                            className={cn(
                                'px-3 py-2 text-sm font-medium border-b-2 -mb-px',
                                'data-[state=active]:text-text data-[state=active]:border-accent',
                                'data-[state=inactive]:text-text-muted data-[state=inactive]:border-transparent hover:text-text',
                            )}
                        >
                            Details
                        </Tabs.Trigger>
                        <Tabs.Trigger
                            value="rates"
                            disabled={!savedId}
                            className={cn(
                                'px-3 py-2 text-sm font-medium border-b-2 -mb-px',
                                'data-[state=active]:text-text data-[state=active]:border-accent',
                                'data-[state=inactive]:text-text-muted data-[state=inactive]:border-transparent hover:text-text',
                                'disabled:opacity-40 disabled:cursor-not-allowed',
                            )}
                            title={!savedId ? 'Save the service type first to configure payer rates' : undefined}
                        >
                            Payer Rates
                        </Tabs.Trigger>
                    </Tabs.List>

                    <Tabs.Content value="details" className="flex-1 min-h-0 data-[state=active]:flex data-[state=active]:flex-col">
                        <DetailsTab
                            initial={initial}
                            onCreate={async (payload) => {
                                const created = await onCreate(payload);
                                setSavedId(created.id);
                                setTab('rates');
                            }}
                            onUpdate={async (id, payload) => {
                                await onUpdate(id, payload);
                                onClose();
                            }}
                        />
                    </Tabs.Content>

                    <Tabs.Content value="rates" className="flex-1 min-h-0 data-[state=active]:flex data-[state=active]:flex-col">
                        {savedId ? <PayerRatesTab serviceTypeId={savedId} onDone={onClose} /> : null}
                    </Tabs.Content>
                </Tabs.Root>
            </DialogContent>
        </Dialog>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Details tab
// ─────────────────────────────────────────────────────────────────────────

function DetailsTab({
    initial,
    onCreate,
    onUpdate,
}: {
    initial: ServiceType | null;
    onCreate: (p: ServiceTypeWritePayload) => Promise<void>;
    onUpdate: (id: string, p: ServiceTypeWritePayload) => Promise<void>;
}) {
    const { data: serviceCodes = [], isLoading: loadingSCs } = useServiceCodes();

    const [serviceCodeId, setServiceCodeId] = useState<string>(initial?.serviceCodeId ?? '');
    const [name, setName] = useState(initial?.name ?? '');
    const [provider, setProvider] = useState<ProviderLevel>(initial?.provider || 'RBT');
    const [billable, setBillable] = useState<boolean>(initial?.billable ?? true);
    const [billRate, setBillRate] = useState<number>(initial?.billRate ?? 0);
    const [allowRate, setAllowRate] = useState<number>(initial?.allowRate ?? 0);
    const [mod1, setMod1] = useState(initial?.mod1 ?? '');
    const [mod2, setMod2] = useState(initial?.mod2 ?? '');
    const [unitDur, setUnitDur] = useState<number>(initial?.unitDur ?? 15);
    const [status, setStatus] = useState<'Active' | 'Inactive'>(initial?.status ?? 'Active');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // Filter dropdown to active codes — but always include the currently-
    // selected code even if it's since been set to Inactive, so editing an
    // existing row doesn't silently blank the field.
    const codeOptions = useMemo(() => {
        const active = serviceCodes.filter((c) => c.status === 'Active');
        if (serviceCodeId && !active.some((c) => c.id === serviceCodeId)) {
            const current = serviceCodes.find((c) => c.id === serviceCodeId);
            if (current) return [...active, current];
        }
        return active;
    }, [serviceCodes, serviceCodeId]);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!serviceCodeId) { setErr('Service code is required'); return; }
        if (!name.trim())   { setErr('Name is required'); return; }
        setSaving(true);
        try {
            const payload: ServiceTypeWritePayload = {
                serviceCodeId,
                name: name.trim(),
                provider,
                billable,
                billRate: Number.isFinite(billRate) ? billRate : 0,
                allowRate: Number.isFinite(allowRate) ? allowRate : 0,
                mod1: mod1.trim(),
                mod2: mod2.trim(),
                unitDur: Number.isFinite(unitDur) ? unitDur : 15,
                status,
            };
            if (initial) await onUpdate(initial.id, payload);
            else         await onCreate(payload);
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-1 -mx-1 grid grid-cols-4 gap-x-3">
                <FormField label="Name" required error={err ?? undefined} className="col-span-4">
                    {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
                </FormField>

                <FormField
                    label="Service code"
                    required
                    hint="Links to a configured CPT/HCPCS code for this practice"
                    className="col-span-2 min-w-0"
                >
                    {(id) => (
                        <select
                            id={id}
                            value={serviceCodeId}
                            onChange={(e) => setServiceCodeId(e.target.value)}
                            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                        >
                            <option value="">{loadingSCs ? 'Loading…' : 'Select…'}</option>
                            {codeOptions.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.code}{c.desc ? ` — ${c.desc}` : ''}
                                </option>
                            ))}
                        </select>
                    )}
                </FormField>
                <FormField
                    label="Provider level"
                    hint="Triggers a scheduling warning if an assigned staff's role doesn't match — Any suppresses the warning"
                    className="col-span-2 min-w-0"
                >
                    {(id) => (
                        <select
                            id={id}
                            value={provider}
                            onChange={(e) => setProvider(e.target.value as ProviderLevel)}
                            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                        >
                            {PROVIDER_OPTIONS.map((p) => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>
                    )}
                </FormField>

                <div className="col-span-4 flex items-center gap-2 mb-3.5">
                    <input
                        id="st-billable"
                        type="checkbox"
                        checked={billable}
                        onChange={(e) => setBillable(e.target.checked)}
                        className="rounded border-border"
                    />
                    <label htmlFor="st-billable" className="text-sm text-text">Billable</label>
                </div>

                <FormField label="Bill rate ($)" hint="What we charge" className="col-span-2 min-w-0">
                    {(id) => (
                        <Input
                            id={id}
                            type="number"
                            step="0.01"
                            min="0"
                            disabled={!billable}
                            value={Number.isFinite(billRate) ? billRate : 0}
                            onChange={(e) => setBillRate(Number(e.target.value))}
                        />
                    )}
                </FormField>
                <FormField label="Default allowed rate ($)" hint="Fallback when no payer-specific rate is configured" className="col-span-2 min-w-0">
                    {(id) => (
                        <Input
                            id={id}
                            type="number"
                            step="0.01"
                            min="0"
                            disabled={!billable}
                            value={Number.isFinite(allowRate) ? allowRate : 0}
                            onChange={(e) => setAllowRate(Number(e.target.value))}
                        />
                    )}
                </FormField>

                <FormField label="Modifier 1" className="col-span-1 min-w-0">
                    {(id) => <Input id={id} value={mod1} onChange={(e) => setMod1(e.target.value)} />}
                </FormField>
                <FormField label="Modifier 2" className="col-span-1 min-w-0">
                    {(id) => <Input id={id} value={mod2} onChange={(e) => setMod2(e.target.value)} />}
                </FormField>
                <FormField label="Unit duration (min)" className="col-span-1 min-w-0">
                    {(id) => (
                        <Input
                            id={id}
                            type="number"
                            min="1"
                            value={Number.isFinite(unitDur) ? unitDur : 15}
                            onChange={(e) => setUnitDur(Number(e.target.value))}
                        />
                    )}
                </FormField>
                <FormField label="Status" className="col-span-1 min-w-0">
                    {(id) => (
                        <select
                            id={id}
                            value={status}
                            onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive')}
                            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                        >
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                        </select>
                    )}
                </FormField>
            </div>

            <DialogFooter className="shrink-0">
                <DialogClose asChild>
                    <Button variant="ghost" size="sm" type="button">Cancel</Button>
                </DialogClose>
                <Button variant="primary" size="sm" type="submit" disabled={saving}>
                    {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                </Button>
            </DialogFooter>
        </form>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Payer Rates tab
// ─────────────────────────────────────────────────────────────────────────

function PayerRatesTab({ serviceTypeId, onDone }: { serviceTypeId: string; onDone: () => void }) {
    const { data: rates = [], isLoading } = usePayerRates(serviceTypeId);
    const { data: payers = [] } = usePayers();
    const createM = useCreatePayerRate();
    const updateM = useUpdatePayerRate();
    const deleteM = useDeletePayerRate();

    const [editing, setEditing] = useState<PayerRate | 'new' | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<PayerRate | null>(null);
    const [deleteErr, setDeleteErr] = useState<string | null>(null);

    const activePayers = useMemo(() => payers.filter((p) => p.status === 'Active'), [payers]);

    const sortedRates = useMemo(
        () => [...rates].sort((a, b) => a.payerName.localeCompare(b.payerName)),
        [rates],
    );

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <div className="shrink-0 flex items-start justify-between gap-3 mb-3">
                <p className="text-xs text-text-subtle">
                    Overrides the default allowed rate for specific payers. Claims use the
                    rate effective on the date of service; if none applies, the service
                    type's default allowed rate is used. Date ranges for the same payer
                    cannot overlap.
                </p>
                <Button variant="primary" size="sm" onClick={() => setEditing('new')} className="shrink-0">
                    <Plus className="w-3.5 h-3.5" /> Add payer rate
                </Button>
            </div>

            <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                    <div className="py-8 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : sortedRates.length === 0 ? (
                    <div className="py-8 text-center text-text-subtle text-sm">
                        No payer-specific rates configured. Claims will use the default allowed rate.
                    </div>
                ) : (
                    <div className="overflow-x-auto border border-border rounded-md">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold">Payer</th>
                                    <th className="px-3 py-2 font-bold text-right w-28">Allowed rate</th>
                                    <th className="px-3 py-2 font-bold w-32">Effective</th>
                                    <th className="px-3 py-2 font-bold w-32">End</th>
                                    <th className="px-3 py-2 font-bold w-24 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRates.map((r) => (
                                    <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-surface-muted transition-colors">
                                        <td className="px-3 py-2 font-medium">{r.payerName}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{USD.format(r.allowRate)}</td>
                                        <td className="px-3 py-2 text-text-muted">{r.effectiveDate || '—'}</td>
                                        <td className="px-3 py-2 text-text-muted">{r.endDate || '—'}</td>
                                        <td className="px-3 py-2 text-right">
                                            <Button variant="ghost" size="xs" aria-label={`Edit rate for ${r.payerName}`} onClick={() => setEditing(r)} className="mr-1">
                                                <Pencil className="w-3 h-3" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="xs"
                                                aria-label={`Delete rate for ${r.payerName}`}
                                                onClick={() => { setDeleteErr(null); setConfirmDelete(r); }}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <DialogFooter className="shrink-0">
                <Button variant="primary" size="sm" type="button" onClick={onDone}>Done</Button>
            </DialogFooter>

            {editing && (
                <PayerRateEditor
                    initial={editing === 'new' ? null : editing}
                    payers={activePayers}
                    existingRates={rates}
                    onCancel={() => setEditing(null)}
                    onSubmit={async ({ payerId, allowRate, effectiveDate, endDate }) => {
                        if (editing === 'new') {
                            await createM.mutateAsync({
                                payerId,
                                serviceTypeId,
                                allowRate,
                                effectiveDate,
                                endDate,
                            });
                        } else {
                            await updateM.mutateAsync({
                                id: editing.id,
                                payload: {
                                    payerId,
                                    allowRate,
                                    effectiveDate,
                                    endDate,
                                },
                            });
                        }
                        setEditing(null);
                    }}
                />
            )}

            <Dialog
                open={!!confirmDelete}
                onOpenChange={(o) => { if (!deleteM.isPending && !o) setConfirmDelete(null); }}
            >
                <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>Delete payer rate?</DialogTitle>
                    </DialogHeader>
                    {confirmDelete && (
                        <p className="text-sm text-text-muted">
                            Delete the payer rate for{' '}
                            <span className="font-medium text-text">{confirmDelete.payerName}</span>?
                        </p>
                    )}
                    {deleteErr && <p className="mt-2 text-xs text-error">{deleteErr}</p>}
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button
                            variant="danger"
                            size="sm"
                            type="button"
                            disabled={deleteM.isPending}
                            onClick={async () => {
                                if (!confirmDelete) return;
                                setDeleteErr(null);
                                try {
                                    await deleteM.mutateAsync(confirmDelete.id);
                                    setConfirmDelete(null);
                                } catch (e) {
                                    setDeleteErr(extractErrorMessage(e, 'Could not delete.'));
                                }
                            }}
                        >
                            {deleteM.isPending ? 'Deleting…' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function PayerRateEditor({
    initial,
    payers,
    existingRates,
    onCancel,
    onSubmit,
}: {
    initial: PayerRate | null;
    payers: Array<{ id: string; name: string; status: 'Active' | 'Inactive' }>;
    existingRates: PayerRate[];
    onCancel: () => void;
    onSubmit: (values: { payerId: string; allowRate: number; effectiveDate: string; endDate: string }) => Promise<void>;
}) {
    const [payerId, setPayerId] = useState<string>(initial?.payerId ?? '');
    const [allowRate, setAllowRate] = useState<number>(initial?.allowRate ?? 0);
    const [effectiveDate, setEffectiveDate] = useState<string>(initial?.effectiveDate ?? '');
    const [endDate, setEndDate] = useState<string>(initial?.endDate ?? '');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // Keep a stale payer option visible when editing an existing rate tied to
    // a now-inactive payer — otherwise the <select> would render empty.
    const payerOptions = useMemo(() => {
        if (payerId && !payers.some((p) => p.id === payerId) && initial?.payerName) {
            return [...payers, { id: payerId, name: initial.payerName, status: 'Inactive' as const }];
        }
        return payers;
    }, [payers, payerId, initial]);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!payerId)                   { setErr('Payer is required'); return; }
        if (!Number.isFinite(allowRate)) { setErr('Allowed rate is required'); return; }
        if (!effectiveDate)             { setErr('Effective date is required'); return; }
        if (!endDate)                   { setErr('End date is required'); return; }
        if (effectiveDate > endDate)    { setErr('End date must be on or after effective date'); return; }

        // Overlap check: [effectiveDate, endDate] (inclusive) must not intersect
        // any other rate for the same payer on this service type.
        const overlap = existingRates.find((r) => {
            if (initial && r.id === initial.id) return false;
            if (r.payerId !== payerId)          return false;
            const rStart = r.effectiveDate || '0000-01-01';
            const rEnd   = r.endDate       || '9999-12-31';
            return rStart <= endDate && rEnd >= effectiveDate;
        });
        if (overlap) {
            setErr(`Date range overlaps an existing rate for ${overlap.payerName} (${overlap.effectiveDate ?? '—'} to ${overlap.endDate ?? '—'})`);
            return;
        }

        setSaving(true);
        try {
            await onSubmit({ payerId, allowRate, effectiveDate, endDate });
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onCancel()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit payer rate' : 'Add payer rate'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <FormField label="Payer" required error={err ?? undefined}>
                        {(id) => (
                            <select
                                id={id}
                                value={payerId}
                                onChange={(e) => setPayerId(e.target.value)}
                                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">Select…</option>
                                {payerOptions.map((p) => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        )}
                    </FormField>
                    <FormField label="Allowed rate ($)" required>
                        {(id) => (
                            <Input
                                id={id}
                                type="number"
                                step="0.01"
                                min="0"
                                value={Number.isFinite(allowRate) ? allowRate : 0}
                                onChange={(e) => setAllowRate(Number(e.target.value))}
                            />
                        )}
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                        <FormField label="Effective date" required>
                            {(id) => <Input id={id} type="date" min="1900-01-01" max="9999-12-31" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />}
                        </FormField>
                        <FormField label="End date" required>
                            {(id) => <Input id={id} type="date" min={effectiveDate || '1900-01-01'} max="9999-12-31" value={endDate} onChange={(e) => setEndDate(e.target.value)} />}
                        </FormField>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" size="sm" type="button" onClick={onCancel}>Cancel</Button>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Delete confirm
// ─────────────────────────────────────────────────────────────────────────

function DeleteConfirmDialog({
    row,
    onClose,
    onConfirm,
}: {
    row: ServiceType;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Delete service type?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    Delete <span className="font-medium text-text">{row.name}</span>? If this
                    service type is referenced by any session or claim the delete will be
                    refused.
                </p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try { await onConfirm(); }
                            catch (e) { setErr(extractErrorMessage(e, 'Could not delete.')); }
                            finally { setWorking(false); }
                        }}
                    >
                        {working ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
